import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Loader2, Save, X } from "lucide-react";

import { useAuth } from "../../../app/providers/AuthProvider";
import { ordersService } from "../../../services/orders";
import { productsService } from "../../../services/products";
import { addressesService } from "../../../services/addresses";
import { formatApiError } from "../../../services/api";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { StepEvent } from "../wizard/steps/StepEvent";
import { StepProducts } from "../wizard/steps/StepProducts";
import { StepAddresses, computeSavePlan } from "../wizard/steps/StepAddresses";
import { StepReview } from "../wizard/steps/StepReview";

const EMPTY_FORM = {
  event_name: "",
  bottles_count: "",
  from_date: null,
  until_date: null,
  remarks: "",
  items: [],
  customer_address_id: null,
  customer_address_inline: null,
  delivery_same_as_customer: true,
  delivery_address_id: null,
  delivery_address_inline: null,
  on_behalf_of_user_id: null,
};

const STEPS = ["event", "products", "addresses", "review"];

function StepPill({ n, label, active, done, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!done && !active}
      data-testid={`wizard-step-${n}`}
      className={`flex items-center gap-2 transition-colors ${!done && !active ? "cursor-not-allowed" : ""}`}
    >
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
          active
            ? "bg-champagne text-charcoal"
            : done
              ? "bg-charcoal text-white"
              : "bg-offwhite text-text-secondary border border-border-subtle"
        }`}
      >
        {done ? <Check className="h-4 w-4" /> : n}
      </span>
      <span className={`text-sm ${active ? "font-semibold text-text-primary" : "text-text-secondary"}`}>
        {label}
      </span>
    </button>
  );
}

export default function OrderWizardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [busy, setBusy] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState(routeId || null);
  const [existingOrder, setExistingOrder] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const isEditing = Boolean(routeId);
  // "Existing draft" = we entered the wizard through /orders/:id (persisted
  // in DB) OR we've hit Save-Draft at least once in this session. Used by
  // the Cancel button to decide between silent-revert vs confirm-discard.
  const isPersistedDraft = Boolean(routeId) || Boolean(savedDraftId);

  // Phase 5: pre-fill from URL params (from=, until=, product_id=) on the new
  // order flow. Skip if editing an existing order.
  useEffect(() => {
    if (isEditing) return;
    const from = searchParams.get("from");
    const until = searchParams.get("until");
    const productId = searchParams.get("product_id");
    if (!from && !until && !productId) return;
    (async () => {
      let product = null;
      if (productId) {
        try {
          product = await productsService.get(productId);
        } catch {
          /* ignore invalid product ids */
        }
      }
      setForm((f) => ({
        ...f,
        from_date: from || f.from_date,
        until_date: until || f.until_date,
        items: product
          ? [{
              product_id: product.id,
              quantity: 1,
              product_name_snapshot: product.name,
              product_image_snapshot:
                (Array.isArray(product.image_urls) && product.image_urls[0]) ||
                product.image_url ||
                null,
              image_urls_snapshot: Array.isArray(product.image_urls)
                ? product.image_urls
                : product.image_url
                ? [product.image_url]
                : [],
            }]
          : f.items,
      }));
    })();
  }, [isEditing, searchParams]);

  // Load existing order if editing
  useEffect(() => {
    if (!routeId) return;
    ordersService
      .get(routeId)
      .then((o) => {
        if (!o.amendable) {
          toast.error(t("wizard.not_amendable"));
          navigate(`/orders/${o.id}`, { replace: true });
          return;
        }
        setExistingOrder(o);
        // A12: `saved_addresses` on the user is retired. Existing orders
        // carry their address inline via the customer / delivery_address
        // snapshots; just replay that shape into the wizard's inline
        // fields. The user can still re-search the global book to swap.
        setForm({
          event_name: o.event_name || "",
          bottles_count: String(o.bottles_count || ""),
          from_date: o.from_date || null,
          until_date: o.until_date || null,
          remarks: o.remarks || "",
          items: (o.items || []).map((i) => ({
            product_id: i.product_id,
            quantity: i.quantity,
            product_name_snapshot: i.product_name_snapshot,
            product_image_snapshot: i.product_image_snapshot,
            image_urls_snapshot: Array.isArray(i.image_urls_snapshot)
              ? i.image_urls_snapshot
              : i.product_image_snapshot
              ? [i.product_image_snapshot]
              : [],
          })),
          customer_address_id: null,
          customer_address_inline: o.customer || null,
          delivery_same_as_customer: !o.delivery_address,
          delivery_address_id: null,
          delivery_address_inline: o.delivery_address || null,
        });
      })
      .catch(() => {
        toast.error(t("common.error_generic"));
        navigate("/orders");
      });
  }, [routeId, navigate, t, user]);

  const canGoNext = useMemo(() => {
    if (step === 0) return form.event_name && form.from_date && form.until_date && Number(form.bottles_count) > 0;
    if (step === 1) return form.items.length > 0;
    // A18.2 — step 2 (addresses) is validated on Continue click via the
    // ref-exposed `validateAndFocus()`. The Continue button is always
    // enabled here so the user gets field-level errors instead of a
    // silently-disabled button.
    return true;
  }, [step, form]);

  const addressesRef = useRef(null);

  const goNext = () => {
    if (step === 2) {
      const ok = addressesRef.current?.validateAndFocus?.();
      if (!ok) return;
      addressesRef.current?.resetErrors?.();
    }
    setStep((s) => s + 1);
  };

  const buildPayload = (draft) => ({
    event_name: form.event_name,
    bottles_count: Number(form.bottles_count) || 0,
    from_date: form.from_date,
    until_date: form.until_date,
    customer_address_id: form.customer_address_id,
    customer_address_inline: form.customer_address_inline,
    delivery_same_as_customer: form.delivery_same_as_customer,
    delivery_address_id: form.delivery_same_as_customer ? null : form.delivery_address_id,
    delivery_address_inline: form.delivery_same_as_customer ? null : form.delivery_address_inline,
    remarks: form.remarks,
    items: form.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    draft,
    on_behalf_of_user_id:
      form.on_behalf_of_user_id && form.on_behalf_of_user_id !== user?.id
        ? form.on_behalf_of_user_id
        : undefined,
  });

  const persistSavePlan = async () => {
    // A12: POST each newly-authored address to the global book.
    // A12.1: no longer silent — 409 (duplicate) is swallowed since that
    // just means "already there", but any other failure surfaces a
    // subtle warning toast so a hidden regression can't lurk again. The
    // order itself is unaffected — the address is still captured inline.
    const plan = computeSavePlan(form);
    let hadFailure = false;
    for (const addr of plan) {
      try {
        // eslint-disable-next-line no-unused-vars
        const { vat_id, ...rest } = addr;
        await addressesService.create({
          ...rest,
          vat_id: (vat_id || "").trim() || null,
        });
      } catch (err) {
        const code = err?.response?.data?.detail?.code;
        if (code === "DUPLICATE_ADDRESS") continue;
        hadFailure = true;
        // eslint-disable-next-line no-console
        console.error("wizard: save-to-book failed", err);
      }
    }
    if (hadFailure) {
      toast.warning(t("wizard.step1.save_to_book_failed"));
    }
    // Clear the flags so we don't re-POST on a subsequent submit.
    setForm((f) => ({
      ...f,
      _save_customer_to_book: false,
      _save_delivery_to_book: false,
    }));
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await persistSavePlan();
      if (savedDraftId) {
        const patch = { ...buildPayload(true) };
        delete patch.draft;
        await ordersService.update(savedDraftId, patch);
      } else {
        const created = await ordersService.create(buildPayload(true));
        setSavedDraftId(created.id);
      }
      toast.success(t("wizard.draft_saved"));
      navigate("/orders?scope=drafts");
    } catch (e) {
      toast.error(formatApiError(e, t("common.error_generic")));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await persistSavePlan();
      let result;
      if (isEditing && existingOrder?.status !== "draft") {
        const patch = { ...buildPayload(false) };
        delete patch.draft;
        result = await ordersService.update(existingOrder.id, patch);
        toast.success(t("wizard.order_updated"));
        navigate(`/orders/${result.id}`);
      } else if (savedDraftId || existingOrder?.status === "draft") {
        const patch = { ...buildPayload(false) };
        delete patch.draft;
        const targetId = savedDraftId || existingOrder.id;
        await ordersService.update(targetId, patch);
        result = await ordersService.submitDraft(targetId);
        setConfirmed(result);
      } else {
        result = await ordersService.create(buildPayload(false));
        setConfirmed(result);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "AVAILABILITY") {
        const names = detail.violations
          .map((v) => `${v.product_name} (${v.requested}/${v.available_min})`)
          .join(", ");
        toast.error(`${t("wizard.avail_violation")}: ${names}`);
        setStep(1);
      } else {
        toast.error(formatApiError(e, t("common.error_generic")));
      }
    } finally {
      setBusy(false);
    }
  };

  // A12 — Cancel button. Two modes:
  //   1) Persisted draft (routeId in URL, or Save-Draft pressed at least
  //      once this session): silently revert unsaved edits, back to drafts.
  //   2) Fresh new draft (no server persistence): confirm-and-discard.
  const onCancel = () => {
    if (isPersistedDraft) {
      toast.success(t("wizard.changes_discarded_toast"));
      navigate("/orders?scope=drafts");
    } else {
      setDiscardOpen(true);
    }
  };
  const confirmDiscardNew = () => {
    setDiscardOpen(false);
    navigate("/orders");
  };

  if (confirmed) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center" data-testid="wizard-confirmation">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-champagne">
          <Check className="h-8 w-8 text-charcoal" />
        </div>
        <h1 className="mt-6 text-2xl font-semibold">{t("wizard.confirmation_title")}</h1>
        <div className="mt-3 font-mono text-3xl" data-testid="wizard-confirmation-number">
          #{confirmed.order_number}
        </div>
        <div className="mt-8 flex justify-center gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="wizard-confirmation-dashboard">
            {t("wizard.back_to_dashboard")}
          </Button>
          <Button
            className="bg-charcoal text-white hover:bg-charcoal-soft"
            onClick={() => navigate(`/orders/${confirmed.id}`)}
            data-testid="wizard-confirmation-detail"
          >
            {t("wizard.view_order_details")}
          </Button>
        </div>
      </div>
    );
  }

  const stepLabels = [
    t("wizard.step_event"),
    t("wizard.step_products"),
    t("wizard.step_addresses"),
    t("wizard.step_review"),
  ];
  const canSaveDraft = !isEditing || existingOrder?.status === "draft";

  return (
    <div className="space-y-6" data-testid="wizard-page">
      <header>
        <h1 className="text-3xl font-semibold text-text-primary" data-testid="wizard-title">
          {isEditing && existingOrder?.status !== "draft"
            ? t("wizard.title_amend", { number: existingOrder?.order_number })
            : t("wizard.title_new")}
        </h1>
      </header>

      <div className="flex flex-wrap items-center gap-3" data-testid="wizard-progress">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <StepPill
              n={i + 1}
              label={stepLabels[i]}
              active={i === step}
              done={i < step}
              onClick={() => i < step && setStep(i)}
            />
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-border-subtle" />}
          </React.Fragment>
        ))}
      </div>

      <div className="rounded-lg border border-border-subtle bg-surface p-6 shadow-card">
        {step === 0 && <StepEvent form={form} setForm={setForm} t={t} />}
        {step === 1 && <StepProducts form={form} setForm={setForm} t={t} />}
        {step === 2 && <StepAddresses ref={addressesRef} form={form} setForm={setForm} t={t} />}
        {step === 3 && <StepReview form={form} t={t} i18n={i18n} />}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || busy}
            data-testid="wizard-back"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> {t("common.back")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={busy}
            data-testid="wizard-cancel"
            className="text-text-secondary hover:text-status-unavailable hover:bg-status-unavailable/10"
          >
            <X className="mr-1 h-4 w-4" /> {t("wizard.cancel_button")}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {canSaveDraft && (
            <Button
              type="button"
              variant="outline"
              onClick={saveDraft}
              disabled={busy || !form.event_name}
              data-testid="wizard-save-draft"
            >
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              {t("wizard.save_draft")}
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={goNext}
              disabled={!canGoNext}
              className="bg-charcoal text-white hover:bg-charcoal-soft disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wizard-next"
            >
              {t("common.continue")} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submit}
              disabled={busy}
              className="bg-champagne text-charcoal hover:bg-champagne/90 font-semibold"
              data-testid="wizard-confirm"
            >
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isEditing && existingOrder?.status !== "draft"
                ? t("wizard.save_changes")
                : t("wizard.place_order")}
            </Button>
          )}
        </div>
      </div>

      {/* A12 — discard-new-draft confirm */}
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent
          data-testid="wizard-discard-modal"
          className="max-w-md border-border-subtle bg-surface text-text-primary"
        >
          <DialogHeader>
            <DialogTitle>{t("wizard.discard_new_draft_title")}</DialogTitle>
            <DialogDescription className="text-text-secondary">
              {t("wizard.discard_new_draft_description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setDiscardOpen(false)}
              data-testid="wizard-discard-cancel"
              className="border-border-subtle text-text-primary hover:bg-offwhite"
            >
              {t("wizard.discard_new_draft_cancel")}
            </Button>
            <Button
              onClick={confirmDiscardNew}
              data-testid="wizard-discard-confirm"
              className="bg-status-unavailable text-white hover:bg-status-unavailable/90"
            >
              {t("wizard.discard_new_draft_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
